import { faBuilding, faPen, faPerson } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { GridColDef } from '@mui/x-data-grid-pro'
import { Link, List, ListItem, Stack, Tooltip, Typography } from '@vapor/material'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { HicDataGridPro } from 'src/app/components/hic-vapor/datagrid/datagrid'
import { useTenant } from 'src/app/hooks/context/context.hook'
import useFormatCurrency from 'src/app/hooks/current-locale/use-format-currency'
import useFormatDate from 'src/app/hooks/current-locale/use-format-date'
import useLocalizeString from 'src/app/hooks/current-locale/use-localized-string'
import { keysOf } from 'src/utils/utils'
import { DepositF } from '../../../../types/reservation-detail.types'
import { DepositRow } from './depostis-grid.types'
import { useHicCommonDataGridProps } from '../../../../../../../components/hic-vapor/datagrid/datagrid'
import { HiCIcon } from 'src/app/components/icons/icon'
import useBreakpoints from 'src/app/hooks/breakpoints/use-breakpoints'
import getFiscalTypeDescription from 'src/app/pages/accounting/documents/documents-utils'
import { IconButton } from '@mui/material'

export type DepositGridProps = {
  deposits: DepositF[]
  readOnly: boolean
  onEditClick: (documentId: string) => void
  selectedDepositIds: string[]
  setSelectedDepositIds: (depositIds: string[]) => void
}

export default function DepositsGrid({ deposits, onEditClick, readOnly, selectedDepositIds, setSelectedDepositIds }: DepositGridProps) {
  const columns = useColumns(onEditClick, readOnly)
  const { md } = useBreakpoints()
  const rows = useRows(deposits)
  const { hicCommonDataGridProProps } = useHicCommonDataGridProps()
  return (
    <HicDataGridPro //
      {...hicCommonDataGridProProps}
      columns={md ? columns : columns.filter((col) => !col.hideable)}
      rows={rows}
      autoHeight
      hideFooter
      checkboxSelection
      disableRowSelectionOnClick
      slotProps={{
        baseCheckbox: {
          size: 'small',
        },
      }}
      rowSelectionModel={selectedDepositIds}
      onRowSelectionModelChange={(rowSelectionModel) => {
        // rowSelectionModel is an array of the deposit ids, because the data grid is using by default the field id to identify the row
        const newDepositIds = rowSelectionModel.map((rowId) => `${rowId}` /* to ensure that the type is a string */)
        setSelectedDepositIds(newDepositIds)
      }}
    />
  )
}

function useColumns(onEditClick: (documentId: string) => void, readOnly: boolean): GridColDef[] {
  const { t } = useTranslation()
  const f = keysOf<DepositRow>()
  const formatDate = useFormatDate()
  const { formatCurrency } = useFormatCurrency()
  const localizeString = useLocalizeString()
  return [
    {
      field: f('numberExtended'),
      headerName: t('pms.booking.reservation.deposits.document'),
      flex: 1,
      minWidth: 100,
      disableColumnMenu: true,
      renderCell: (params) => {
        const row = params.row as DepositRow
        return (
          <Stack direction="column">
            <Typography variant="caption">{getFiscalTypeDescription(row.fiscalType, t).toUpperCase()}</Typography>
            <Link style={{ pointerEvents: readOnly ? 'none' : undefined, color: readOnly ? 'var(--Cadet)' : undefined }} onClick={() => onEditClick(params.row.id)}>
              {params.value}
            </Link>
          </Stack>
        )
      },
    },
    {
      field: f('date'),
      headerName: t('pms.booking.reservation.deposits.date'),
      flex: 1,
      minWidth: 100,
      hideable: true,
      disableColumnMenu: true,
      renderCell: (params) => {
        const date = (params.row as DepositRow).date
        return formatDate(date)
      },
    },
    {
      field: f('customer'),
      headerName: t('pms.booking.reservation.deposits.type.owner'),
      flex: 1,
      minWidth: 150,
      disableColumnMenu: true,
      renderCell: (params) => {
        const customer = (params.row as DepositRow).customer
        return (
          <Stack direction="column">
            <Stack direction="row" spacing={0.5} alignItems="center">
              <HiCIcon>
                <FontAwesomeIcon icon={customer.type === 'PERSON' ? faPerson : faBuilding} />
              </HiCIcon>
              <Typography variant="body">{customer.name}</Typography>
            </Stack>
            <Typography variant="caption">{customer.fiscalCode}</Typography>
          </Stack>
        )
      },
    },
    {
      field: 'paymentTypesCol',
      headerName: t('pms.booking.reservation.deposits.payment_type'),
      flex: 1,
      minWidth: 150,
      hideable: true,
      disableColumnMenu: true,
      renderCell: (params) => {
        const paymentsTypes = (params.row as DepositRow).paymentTypes
        if (paymentsTypes.length <= 0) {
          return <></>
        } else if (paymentsTypes.length === 1) {
          return localizeString(paymentsTypes[0])
        } else {
          return (
            <Tooltip
              title={
                <List>
                  {paymentsTypes.map((pt, index) => (
                    <ListItem key={index}>{localizeString(pt)}</ListItem>
                  ))}
                </List>
              }
            >
              <div>{t('pms.booking.reservation.deposits.payment_type.many')}</div>
            </Tooltip>
          )
        }
      },
    },
    {
      field: f('amount'),
      headerName: t('pms.booking.reservation.deposits.amount'),
      flex: 1,
      minWidth: 100,
      disableColumnMenu: true,
      renderCell: (params) => {
        const row = params.row as DepositRow
        return formatCurrency(row.amount, row.currency)
      },
    },
    {
      field: f('residual'),
      headerName: t('pms.booking.reservation.deposits.residual'),
      flex: 1,
      minWidth: 100,
      disableColumnMenu: true,
      renderCell: (params) => {
        const row = params.row as DepositRow
        return formatCurrency(row.residual, row.currency)
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: t('commons.table_actions'),
      getActions: (params) => {
        const row = params.row as DepositRow
        return [
          <IconButton disabled={readOnly} size="small" onClick={() => onEditClick(row.id)} color="primary">
            <FontAwesomeIcon icon={faPen} style={{ fontSize: '1rem' }} />
          </IconButton>,
        ]
      },
    },
  ]
}

function useRows(deposits: DepositF[]): DepositRow[] {
  const { t } = useTranslation()
  const { currency } = useTenant()
  const rows = useMemo(() => {
    const elements: DepositRow[] = deposits.map((d) => {
      return {
        id: d.id,
        numberExtended: d.numberExtended,
        fiscalType: d.fiscalType,
        date: d.documentDate,
        paymentTypes: d.documentPaymentDetails.map((dpd) => dpd.paymentActive.name),
        currency: currency,
        amount: d.totalAmount, //TODO filter per reservationId,
        residual: d.residualAmount ?? 0,
        customer: d.customer,
      }
    })

    return elements
  }, [JSON.stringify(deposits)])

  return rows
}
